import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import ReplayIcon from '@mui/icons-material/Replay';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EmailIcon from '@mui/icons-material/Email';
import { SearchInvoicePublic, PayInvoicePublic, DownloadInvoicePdfPublic, EmailInvoicePdfPublic } from '../../REST/invoices';
import { PayCardStripe, PayAchStripe } from '../WaitList/stripePayment';

const RELATIONS = ['Self', 'Tenant', 'Family Member', 'Friend', 'Business Partner', 'Other'];

export default function PublicPayInvoice() {
    const navigate = useNavigate();
    const [invoiceNumber, setInvoiceNumber] = useState('');
    const [searching, setSearching] = useState(false);
    const [searchError, setSearchError] = useState(null);
    const [invoice, setInvoice] = useState(null);

    const [payer, setPayer] = useState({ name: '', email: '', relation: '' });
    const [payerError, setPayerError] = useState(null);

    const [receipt, setReceipt] = useState(null);

    const [downloading, setDownloading] = useState(false);
    const [emailOpen, setEmailOpen] = useState(false);
    const [emailAddress, setEmailAddress] = useState('');
    const [emailSending, setEmailSending] = useState(false);
    const [emailStatus, setEmailStatus] = useState(null);

    const [payAmount, setPayAmount] = useState('');
    const [payMethod, setPayMethod] = useState('Card');
    const [chequeNum, setChequeNum] = useState('');

    const handleSearch = async () => {
        if (!invoiceNumber.trim()) return;
        setSearching(true);
        setSearchError(null);
        setInvoice(null);
        try {
            const { data } = await SearchInvoicePublic(invoiceNumber.trim());
            setInvoice(data);
            setPayAmount(data.amountDue > 0 ? Number(data.amountDue).toFixed(2) : '');
            if (!data.lineItems?.length) {
                setSearchError('This invoice has no outstanding balance.');
            }
        } catch (ex) {
            setSearchError(ex.response?.data?.message || 'Invoice not found. Please check the invoice number and try again.');
        } finally {
            setSearching(false);
        }
    }

    const handleDownloadReceipt = async () => {
        if (!invoice?._id) return;
        setDownloading(true);
        try {
            const { data } = await DownloadInvoicePdfPublic(invoice._id);
            const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
            window.open(url, '_blank');
            const a = document.createElement('a');
            a.href = url;
            a.download = `Receipt-${invoice.invoiceNumber}.pdf`;
            a.click();
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (ex) {
            setSearchError('Unable to download receipt. Please try again.');
        } finally {
            setDownloading(false);
        }
    }

    const handleSendEmail = async () => {
        if (!invoice?._id || !/\S+@\S+\.\S+/.test(emailAddress)) return;
        setEmailSending(true);
        setEmailStatus(null);
        try {
            await EmailInvoicePdfPublic(invoice._id, { email: emailAddress });
            setEmailStatus({ type: 'success', message: `Invoice sent to ${emailAddress}.` });
        } catch (ex) {
            setEmailStatus({ type: 'error', message: ex.response?.data?.message || 'Unable to send email. Please try again.' });
        } finally {
            setEmailSending(false);
        }
    }

    const isOffline = payMethod === 'Cheque';
    const amountToCharge = payAmount === '' ? 0 : Math.max(0, Number(payAmount) || 0);
    const balanceDiff = invoice ? Math.round((invoice.amountDue - amountToCharge) * 100) / 100 : 0;
    const amountValid = invoice ? (amountToCharge > 0) : false;
    const canPay = payer.name.trim() && /\S+@\S+\.\S+/.test(payer.email) && payer.relation && amountValid && (payMethod !== 'Cheque' || chequeNum.trim());

    const handlePaymentComplete = async (paymentReceipt) => {
        try {
            const { data } = await PayInvoicePublic(invoice._id, {
                receipt: paymentReceipt,
                lineItems: invoice.lineItems,
                payer,
                paidAmount: amountToCharge
            });
            setReceipt({ ...paymentReceipt, amount: amountToCharge });
        } catch (ex) {
            setSearchError(ex.response?.data?.message || 'Payment succeeded but recording it failed. Please contact support with your confirmation.');
        }
    }

    if (receipt) {
        const rows = [
            { label: 'Invoice Number', value: invoice.invoiceNumber },
            { label: 'Amount Paid', value: `$${Number(receipt.amount).toFixed(2)}` },
            { label: 'Payment Method', value: receipt.accountName || payMethod || 'Credit / Debit Card' },
            { label: 'Paid By', value: `${payer.name} (${payer.relation})` },
            { label: 'Date', value: new Date(receipt.paymentDate || Date.now()).toLocaleString() },
            { label: 'Confirmation #', value: receipt.confirmation, mono: true },
        ];

        const resetForm = () => {
            setReceipt(null); setInvoice(null); setInvoiceNumber(''); setPayer({ name: '', email: '', relation: '' });
            setEmailOpen(false); setEmailAddress(''); setEmailStatus(null); setPayAmount('');
        }

        const handleDownload = () => {
            const text = rows.map(r => `${r.label}: ${r.value}`).join('\n');
            const blob = new Blob([`Payment Receipt\n\n${text}\n`], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Receipt-${invoice.invoiceNumber}.txt`;
            a.click();
            URL.revokeObjectURL(url);
        }

        return (
            <Container maxWidth="sm" sx={{ py: { xs: 4, sm: 8 } }}>
                <Paper elevation={2} sx={{ borderRadius: 2, p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <IconButton onClick={() => navigate(-1)} size="small">
                            <ArrowBackIcon />
                        </IconButton>
                    </Box>
                    <Box sx={{
                        width: 72, height: 72, borderRadius: '50%',
                        bgcolor: 'success.light', color: 'success.dark',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        mx: 'auto', mb: 2
                    }}>
                        <CheckCircleIcon sx={{ fontSize: 40 }} />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>Payment Successful</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>
                        Your payment has been processed successfully. Please save this receipt for your records.
                    </Typography>

                    <Paper variant="outlined" sx={{ borderRadius: 2, textAlign: 'left', overflow: 'hidden' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 2, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Box sx={{
                                width: 36, height: 36, borderRadius: 1.5, bgcolor: 'primary.light',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                            }}>
                                <DescriptionIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Payment Receipt</Typography>
                                <Typography variant="caption" color="text.secondary">Transaction completed successfully</Typography>
                            </Box>
                        </Box>

                        <Box sx={{ px: 2.5 }}>
                            {rows.map((row, i) => (
                                <Box
                                    key={row.label}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        gap: 2,
                                        py: 1.5,
                                        borderBottom: i < rows.length - 1 ? '1px solid' : 'none',
                                        borderColor: 'divider'
                                    }}
                                >
                                    <Typography variant="body2" color="text.secondary">{row.label}</Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 600, fontFamily: row.mono ? 'monospace' : 'inherit', textAlign: 'right', wordBreak: 'break-all' }}
                                    >
                                        {row.value}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Paper>

                    <Box sx={{ display: 'flex', gap: 1.5, mt: 3 }}>
                        <Button sx={{ flex: 1, height: 42 }} variant="contained" startIcon={<DownloadIcon />} onClick={handleDownload}>
                            Download Receipt
                        </Button>
                        <Button sx={{ flex: 1, height: 42 }} variant="outlined" startIcon={<ReplayIcon />} onClick={resetForm}>
                            Pay Another Invoice
                        </Button>
                    </Box>
                </Paper>
            </Container>
        );
    }

    return (
        <Container maxWidth="sm" sx={{ py: 6 }}>
            <Paper elevation={3} sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <IconButton onClick={() => navigate(-1)} size="small">
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h5">Pay an Invoice</Typography>
                </Box>
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                    Enter your invoice number to look up your balance and pay online.
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <TextField
                        fullWidth
                        label="Invoice Number"
                        value={invoiceNumber}
                        onChange={e => setInvoiceNumber(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleSearch()}
                    />
                    <Button variant="contained" onClick={handleSearch} disabled={searching}>
                        {searching ? <CircularProgress size={22} /> : 'Search'}
                    </Button>
                </Box>

                {searchError && <Alert severity="error" sx={{ mb: 2 }}>{searchError}</Alert>}

                {invoice && (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <Typography sx={{ mb: 1 }}><b>Invoice #:</b> {invoice.invoiceNumber}</Typography>
                        <Typography variant="h6" sx={{ mb: 2 }}>Amount Due: ${Number(invoice.amountDue).toFixed(2)}</Typography>

                        {!(invoice.lineItems?.length > 0) && (
                            <>
                                <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                                    <Button
                                        sx={{ flex: 1 }} variant="outlined" startIcon={downloading ? <CircularProgress size={18} /> : <DownloadIcon />}
                                        onClick={handleDownloadReceipt} disabled={downloading}
                                    >
                                        Download Receipt
                                    </Button>
                                    <Button
                                        sx={{ flex: 1 }} variant="outlined" startIcon={<EmailIcon />}
                                        onClick={() => { setEmailOpen(o => !o); setEmailStatus(null); }}
                                    >
                                        Email Receipt
                                    </Button>
                                </Box>

                                {emailOpen && (
                                    <Box sx={{ mb: 2, p: 2, borderRadius: 1, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider' }}>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <TextField
                                                fullWidth size="small" label="Email Address" type="email" value={emailAddress}
                                                onChange={e => setEmailAddress(e.target.value)}
                                            />
                                            <Button
                                                variant="contained" onClick={handleSendEmail}
                                                disabled={emailSending || !/\S+@\S+\.\S+/.test(emailAddress)}
                                            >
                                                {emailSending ? <CircularProgress size={20} /> : 'Send'}
                                            </Button>
                                        </Box>
                                        {emailStatus && <Alert severity={emailStatus.type} sx={{ mt: 1.5 }}>{emailStatus.message}</Alert>}
                                    </Box>
                                )}
                            </>
                        )}

                        {invoice.lineItems?.length > 0 && (
                            <>
                        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>Your Information</Typography>
                        <TextField
                            fullWidth margin="dense" label="Your Name" value={payer.name}
                            onChange={e => setPayer({ ...payer, name: e.target.value })}
                        />
                        <TextField
                            fullWidth margin="dense" label="Your Email" type="email" value={payer.email}
                            onChange={e => setPayer({ ...payer, email: e.target.value })}
                        />
                        <TextField
                            fullWidth margin="dense" select label="Relation to Account" value={payer.relation}
                            onChange={e => setPayer({ ...payer, relation: e.target.value })}
                            SelectProps={{ native: true }}
                        >
                            <option value=""></option>
                            {RELATIONS.map(r => <option key={r} value={r}>{r}</option>)}
                        </TextField>

                        <TextField
                            select
                            fullWidth
                            margin="dense"
                            label="Payment Method"
                            value={payMethod}
                            onChange={e => {
                                setPayMethod(e.target.value);
                            }}
                            SelectProps={{ native: true }}
                        >
                            <option value="Card">Credit / Debit Card</option>
                            <option value="Cheque">Cheque (Paper)</option>
                            <option value="ACH">e-Cheque / ACH</option>
                        </TextField>

                        {payMethod === 'Cheque' && (
                            <TextField
                                fullWidth
                                margin="dense"
                                label="Cheque Number"
                                value={chequeNum}
                                onChange={e => setChequeNum(e.target.value)}
                            />
                        )}

                        <TextField
                            fullWidth margin="dense" type="number" label="Amount to Pay" value={payAmount}
                            error={payAmount !== '' && !amountValid}
                            helperText={
                                payAmount !== '' && !amountValid
                                    ? `Enter an amount greater than $0.`
                                    : (amountToCharge > invoice.amountDue
                                        ? `Amount Due: $${Number(invoice.amountDue).toFixed(2)}. Overpayment of $${(amountToCharge - invoice.amountDue).toFixed(2)} will be credited to tenant balance.`
                                        : `Amount Due: $${Number(invoice.amountDue).toFixed(2)}`)
                            }
                            inputProps={{ step: 0.01, min: 0.01 }}
                            onChange={e => setPayAmount(e.target.value)}
                        />

                        {balanceDiff > 0 && amountValid && !isOffline && (
                            <Alert severity="warning" sx={{ mt: 1 }}>
                                Remaining ${balanceDiff.toFixed(2)} will stay open on this invoice as still due.
                            </Alert>
                        )}

                        {canPay ? (
                            <Box sx={{ mt: 3 }}>
                                {payMethod === 'Card' && (
                                    <PayCardStripe amount={amountToCharge} invoiceId={invoice._id} onComplete={handlePaymentComplete} />
                                )}
                                {payMethod === 'ACH' && (
                                    <PayAchStripe amount={amountToCharge} invoiceId={invoice._id} payerEmail={payer.email} onComplete={handlePaymentComplete} />
                                )}
                                {payMethod === 'Cheque' && (
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        size="large"
                                        onClick={() => handlePaymentComplete({
                                            accountName: 'Cheque',
                                            accountDetails: `Cheque #${chequeNum}`,
                                            paymentDate: new Date().toISOString(),
                                            confirmation: chequeNum
                                        })}
                                    >
                                        Record Cheque Payment
                                    </Button>
                                )}
                            </Box>
                        ) : (
                            <Alert severity="info" sx={{ mt: 3 }}>
                                {amountValid
                                    ? (payMethod === 'Cheque' && !chequeNum.trim()
                                        ? 'Please enter the cheque number to continue.'
                                        : 'Please fill in your name, email, and relation to continue.')
                                    : 'Enter a valid amount to continue to payment.'}
                            </Alert>
                        )}
                            </>
                        )}
                    </>
                )}
            </Paper>
        </Container>
    );
}
